import { Autocomplete, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import * as React from "react";
import ComboBoxQuery from "./ComboBoxQuery";
import { QueryFilter } from "./Interface.helper";
import { useCallback, useEffect, useState } from "react";
import MapView from "@arcgis/core/views/MapView";
import { EPSG3857, EPSG4326 } from "../utils/EPSG";
import { projXY } from "../SearchComponents/SearchComponents.helper";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Map from "@arcgis/core/Map";

interface QueryFilterAreaParams {
  //queryKey: string;
  //selectedValue: (e: string) => void;
  view?: MapView;
  map?: Map;
  queryAreaFilter: (e: any) => void;
}

const queryFilterArea: QueryFilter[] = [
  {
    key: "bbox",
    value: "Bonding box",
  },
  {
    key: "extend",
    value: "Extend",
  },
  {
    key: "area",
    geometry: '(poly:"52.1 21.0 52.2 21.1 52.1 21.1 52.1 21.0");',
    value: "Area",
  },
  {
    key: "distance",
    value: "Distance",
  },
];

type SelectedArea = "bbox" | "extend" | "area" | "distance";

const QueryFilterArea: React.FC<QueryFilterAreaParams> = ({
  view,
  map,
  queryAreaFilter,
}) => {
  const [selectedArea, setSelectedArea] = useState<SelectedArea>(
    queryFilterArea[0].key as SelectedArea
  );

  const [extendValue, setExtendValue] = useState({
    xmin: 0,
    ymin: 0,
    xmax: 0,
    ymax: 0,
  });
  const [centerValue, setCenterValue] = useState({
    lat: 0,
    lng: 0,
  });
  const [items, setItems] = useState<any[]>([]);
  const [distance, setDistance] = useState<Number>(500);
  const [graphicsLayer, setGraphicsLayer] = useState<GraphicsLayer>();

  useEffect(() => {
    if (!map) return;
    const gl = map.findLayerById("draw");
    if (gl instanceof GraphicsLayer) {
      setGraphicsLayer(gl);

      const handle = gl.graphics.on("change", () => {
        if (gl.graphics.length === 0) {
          setItems([]);
        }
      });

      return () => {
        handle.remove();
      };
    }
  }, [map, view]);

  useEffect(() => {
    if (!graphicsLayer) return;
    graphicsLayer.graphics.on("change", updateGraphics);
  }, [graphicsLayer]);

  const updateGraphics = useCallback(() => {
    if (!graphicsLayer) return;
    const currentGraphics = graphicsLayer.graphics.toArray();
    if (currentGraphics.length !== items.length) {
      setItems(
        currentGraphics
          .filter((e) => e.geometry.type === "polygon")
          .map((e, index) => {
            return {
              label: e.attributes?.name ?? 'Geometry',
              id: index,
              geometry: e.geometry,
            };
          })
      );
    }
  }, [graphicsLayer, items.length]);

  useEffect(() => {
    queryAreaFilter(
      `(${extendValue.ymin},${extendValue.xmin},${extendValue.ymax},${extendValue.xmax})`
    );
  }, [extendValue]);

  useEffect(() => {
    queryAreaFilter(
      `;node(around:${distance},${centerValue.lat},${centerValue.lng})`
    );
  }, [centerValue.lat, centerValue.lng, distance]);

  useEffect(() => {
    if (!view) return;

    const handleExtentChange = view.watch("stationary", (isStationary) => {
      if (!isStationary) return;
      if (selectedArea === "bbox") {
        const min = projXY(
          view.extent.xmin,
          view.extent.ymin,
          EPSG3857,
          EPSG4326
        );
        const max = projXY(
          view.extent.xmax,
          view.extent.ymax,
          EPSG3857,
          EPSG4326
        );
        setExtendValue({
          xmin: min[0],
          ymin: min[1],
          xmax: max[0],
          ymax: max[1],
        });
      }
    });

    return () => {
      handleExtentChange.remove();
    };
  }, [view, selectedArea]);

  const handleChangeArea = (selArea: SelectedArea) => {
    if (selArea === "extend") {
      const extent = view?.extent;
      if (!extent) return;
      const min = projXY(extent.xmin, extent.ymin, EPSG3857, EPSG4326);
      const max = projXY(extent.xmax, extent.ymax, EPSG3857, EPSG4326);
      setExtendValue({
        xmin: min[0],
        ymin: min[1],
        xmax: max[0],
        ymax: max[1],
      });
    }
    if (selectedArea === "distance") {
      if (!view) return;
      const center = projXY(view.center.x, view.center.y, EPSG3857, EPSG4326);

      setCenterValue({ lat: center[0], lng: center[1] });
    }
  };

  const handleArea = (geom: __esri.Polygon) => {
    console.log(geom);

    const cords = geom.rings[0].map((e) =>
      projXY(e[0], e[1], EPSG3857, EPSG4326).reverse()
    );

    const closeCords = [...cords, cords[0]].flat(2).join(" ");
    queryAreaFilter(`(poly:"${closeCords}")`);
  };

  return (
    <>
      <Grid size={{ xs: 12, md: 12 }}>
        <ComboBoxQuery
          queryFilter={queryFilterArea}
          defaultValue={queryFilterArea[0]}
          selectedValue={(e) => {
            setSelectedArea(e);
            handleChangeArea(e);
          }}
        ></ComboBoxQuery>
      </Grid>

      {selectedArea === "distance" && (
        <>
          <TextField
            style={{ width: "95%", marginLeft: "5px" }}
            size="small"
            value={distance}
            label="Distance"
            variant="standard"
            onChange={(e) => {
              setDistance(Number(e.target.value));
            }}
          />
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Lat"
              defaultValue={centerValue.lat}
              onChange={(e) => {
                setCenterValue((prevState) => ({
                  ...prevState,
                  lat: Number(e.target.value),
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Lng"
              defaultValue={centerValue.lng}
              onChange={(e) => {
                setCenterValue((prevState) => ({
                  ...prevState,
                  lng: Number(e.target.value),
                }));
              }}
            />
          </Grid>
        </>
      )}

      {selectedArea === "area" && (
        <Autocomplete
          disablePortal
          disableCloseOnSelect
          options={items}
          sx={{ width: "95%", paddingLeft: "5px" }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" label="Polygon" />
          )}
          onChange={(_, v) => {
            if (!v || !("geometry" in v)) return;
            handleArea(v.geometry);
          }}
        />
      )}

      {selectedArea === "extend" && (
        <>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Xmin"
              defaultValue={extendValue.xmin}
              onChange={(e) => {
                setExtendValue((prevState) => ({
                  ...prevState,
                  xmin: Number(e.target.value),
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Ymin"
              defaultValue={extendValue.ymin}
              onChange={(e) => {
                setExtendValue((prevState) => ({
                  ...prevState,
                  ymin: Number(e.target.value),
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Xmax"
              defaultValue={extendValue.xmax}
              onChange={(e) => {
                setExtendValue((prevState) => ({
                  ...prevState,
                  xmax: Number(e.target.value),
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              style={{ width: "95%", marginLeft: "5px" }}
              label="Ymax"
              defaultValue={extendValue.ymax}
              onChange={(e) => {
                setExtendValue((prevState) => ({
                  ...prevState,
                  ymax: Number(e.target.value),
                }));
              }}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default QueryFilterArea;
